import React from 'react';
import { Button } from '../../ui/button';
import { useAdminCtx } from '../AdminDashboardContext';

export default function FunnelTab() {
  const { dashboard, fetchFunnelStats, funnelStats } = useAdminCtx();
  return (
    <div className="space-y-6" data-testid="funnel-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Funnel Performance</h2>
        <Button onClick={fetchFunnelStats} variant="outline" size="sm" className="text-slate-400 hover:text-white">
          Refresh
        </Button>
      </div>

      {funnelStats ? (
        <>
          {/* Top Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Total Leads</p>
              <p className="text-3xl font-bold text-white">{funnelStats.total_leads}</p>
            </div>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Avg Alignment Score</p>
              <p className="text-3xl font-bold text-cyan-400">{funnelStats.avg_alignment_score}%</p>
            </div>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Booked Calls</p>
              <p className="text-3xl font-bold text-emerald-400">{funnelStats.conversion?.booked_calls || 0}</p>
            </div>
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Conversion Rate</p>
              <p className="text-3xl font-bold text-violet-400">{funnelStats.conversion?.conversion_rate || 0}%</p>
            </div>
          </div>

          {/* Score Distribution */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-white font-semibold mb-4">Score Distribution</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-2xl font-bold text-emerald-400">{funnelStats.score_distribution?.high || 0}</p>
                <p className="text-emerald-400 text-sm font-medium">High (80+)</p>
                <p className="text-slate-500 text-xs">{funnelStats.score_distribution?.high_pct || 0}%</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-2xl font-bold text-amber-400">{funnelStats.score_distribution?.medium || 0}</p>
                <p className="text-amber-400 text-sm font-medium">Medium (60-79)</p>
                <p className="text-slate-500 text-xs">{funnelStats.score_distribution?.medium_pct || 0}%</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-2xl font-bold text-red-400">{funnelStats.score_distribution?.low || 0}</p>
                <p className="text-red-400 text-sm font-medium">Low (&lt;60)</p>
                <p className="text-slate-500 text-xs">{funnelStats.score_distribution?.low_pct || 0}%</p>
              </div>
            </div>
          </div>

          {/* Email Stats */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-white font-semibold mb-4">Email Performance</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-slate-800/50">
                <p className="text-2xl font-bold text-white">{funnelStats.email_stats?.total_sent || 0}</p>
                <p className="text-slate-400 text-sm">Emails Sent</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-2xl font-bold text-cyan-400">{funnelStats.email_stats?.open_rate || 0}%</p>
                <p className="text-cyan-400 text-sm">Open Rate</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-violet-500/10 border border-violet-500/30">
                <p className="text-2xl font-bold text-violet-400">{funnelStats.email_stats?.click_rate || 0}%</p>
                <p className="text-violet-400 text-sm">Click Rate</p>
              </div>
            </div>
          </div>

          {/* Conversion Funnel Visual */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h3 className="text-white font-semibold mb-4">Conversion Funnel</h3>
            <div className="space-y-3">
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 text-sm">Assessment Completed</span>
                  <span className="text-white font-semibold">{funnelStats.total_leads}</span>
                </div>
                <div className="h-8 bg-slate-700 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-lg" style={{ width: '100%' }}></div>
                </div>
              </div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 text-sm">Emails Opened</span>
                  <span className="text-white font-semibold">{funnelStats.email_stats?.open_rate || 0}%</span>
                </div>
                <div className="h-8 bg-slate-700 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-violet-500 to-violet-600 rounded-lg" style={{ width: `${funnelStats.email_stats?.open_rate || 0}%` }}></div>
                </div>
              </div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 text-sm">Links Clicked</span>
                  <span className="text-white font-semibold">{funnelStats.email_stats?.click_rate || 0}%</span>
                </div>
                <div className="h-8 bg-slate-700 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg" style={{ width: `${funnelStats.email_stats?.click_rate || 0}%` }}></div>
                </div>
              </div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 text-sm">Booked Call</span>
                  <span className="text-white font-semibold">{funnelStats.conversion?.conversion_rate || 0}%</span>
                </div>
                <div className="h-8 bg-slate-700 rounded-lg overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg" style={{ width: `${funnelStats.conversion?.conversion_rate || 0}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
          <p className="text-slate-500">Loading funnel stats...</p>
        </div>
      )}
    </div>
  );
}
